import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { compose } from '../../common/helpers'
import { withTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { connect } from 'react-redux'
import { Header } from '.'
import { APP_VERSION, appTypeConfig } from '../../common/config'
import detectOs from '../../common/lib/detectOs'
import BrowserSupport from './BrowserSupport'
import AppVersionReloader from './AppVersionReloader'
import { Link } from './Link'

const COMPACT_FOOTER_PADDING = '12px'

/** Used by BulkOrderPage: fillViewport = fixed viewport height; compactFooter = smaller footer. See browser/bulkOrder/README.md */
const PageContainer = styled.div`
  background-color: ${({ theme, backgroundColor }) =>
    backgroundColor || theme.colors.pageBackground};
  min-height: 100%;
  padding-bottom: ${({ compactFooter }) => (compactFooter ? '0' : '200px')};

  ${({ fillViewport }) =>
    fillViewport &&
    css`
      height: calc(100vh - 6rem);
      min-height: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    `}
`

const ContentWrapper = styled.div`
  padding: ${({ theme }) => theme.page.paddingHorizontal};
  padding-top: ${({ theme }) => theme.page.paddingTop};
  padding-bottom: ${({ theme, compactFooter }) =>
    compactFooter ? COMPACT_FOOTER_PADDING : theme.page.paddingBottom};

  ${({ fillViewport }) =>
    fillViewport &&
    css`
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    `}
`

const Content = styled.div`
  max-width: ${({ theme, maxWidth }) => maxWidth || theme.page.maxWidth};
  min-width: ${({ theme, minWidth }) => minWidth || theme.page.minWidth};
  margin: auto;

  ${({ fillViewport }) =>
    fillViewport &&
    css`
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      width: 100%;
    `}
`

const Footer = styled.div`
  text-align: center;
  color: #b2b2b2;
  padding-bottom: ${({ compactFooter }) =>
    compactFooter ? COMPACT_FOOTER_PADDING : '3.5rem'};
  font-size: 1.4rem;
  flex-shrink: 0;
`

const SupportLink = styled.a`
  color: #b2b2b2;
`
const FooterLink = styled(Link)`
  color: #b2b2b2;
  font-size: 1.4rem;
  text-decoration: underline;
`

const ContentContainer = ({ fillViewport, compactFooter, ...props }) => (
  <ContentWrapper fillViewport={fillViewport} compactFooter={compactFooter}>
    <Content fillViewport={fillViewport} {...props} />
  </ContentWrapper>
)

class Page extends React.Component {
  state = {
    os: 'Windows',
  }

  componentDidMount() {
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      os: detectOs(),
    })
  }

  render() {
    const {
      metaTitle,
      children,
      backgroundColor,
      fillViewport,
      compactFooter,
      viewer,
      t,
      i18n: { language },
    } = this.props
    const { os } = this.state

    return (
      <PageContainer
        backgroundColor={backgroundColor}
        fillViewport={fillViewport}
        compactFooter={compactFooter}
      >
        <Helmet title={metaTitle} />
        {children}
        <Footer compactFooter={compactFooter}>
          Version {APP_VERSION}
          {appTypeConfig.versionPrefix} - {viewer ? `Login: ${viewer.username}` : null}
          {' - '}
          <SupportLink
            target="_blank"
            href={
              os === 'Mac OS'
                ? 'https://download.teamviewer.com/download/TeamViewerQS.dmg'
                : 'https://download.teamviewer.com/download/TeamViewerQS.exe'
            }
          >
            {t('Support download')} ({os})
          </SupportLink>
          {appTypeConfig.showFooterImpressum && (
            <>
              {' - '}
              <FooterLink to={Link.OWNER}>Impressum</FooterLink>
            </>
          )}
          {appTypeConfig.showFooterLegalDoc && (
            <>
              {' - '}
              <SupportLink
                target="_blank"
                href={`/public/legal/legal-${appTypeConfig.APP_TYPE}.pdf`}
              >
                {t('footer - legal doc link')}
              </SupportLink>
            </>
          )}
          {appTypeConfig.showFooterGdprDoc && (
            <>
              {' - '}
              <SupportLink
                target="_blank"
                href={`/public/gdpr/gdpr-${appTypeConfig.APP_TYPE}-${language}.html`}
              >
                GDPR
              </SupportLink>
            </>
          )}
        </Footer>
      </PageContainer>
    )
  }
}

Page.defaultProps = {
  backgroundColor: '',
  fillViewport: false,
  compactFooter: false,
}

Page.propTypes = {
  children: PropTypes.node.isRequired,
  metaTitle: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
  fillViewport: PropTypes.bool,
  compactFooter: PropTypes.bool,
}

const Extended = compose(
  connect(({ app }) => ({
    viewer: app.viewer,
  })),
  withTranslation()
)(Page)

Extended.Content = ContentContainer

export default Extended
